/**
 * Mobile Authentication Service
 * 
 * Handles authentication synchronization between the mobile app and web app.
 * This service manages the authentication flow when users sign in via the mobile
 * app's native authentication and need to be authenticated in the WebView.
 */

import { auth } from './firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { getUserProfile } from '@/services/userProfileService';
import { isMobileWebView } from './mobileDetection';
import { toast } from '@/hooks/use-toast';

interface MobileAuthData {
  uid: string;
  email: string;
  idToken: string;
  timestamp: string;
  source: 'mobile_app';
}

export class MobileAuthService {
  private static instance: MobileAuthService;
  private initialized = false;

  private constructor() {
    this.initialize();
  }

  public static getInstance(): MobileAuthService {
    if (!MobileAuthService.instance) {
      MobileAuthService.instance = new MobileAuthService();
    }
    return MobileAuthService.instance;
  }

  private initialize(): void {
    if (this.initialized || !isMobileWebView()) return;

    // Listen for mobile auth events
    document.addEventListener('mobileAuthSuccess', this.handleMobileAuthEvent.bind(this));

    // Check for existing auth data on page load
    this.checkStoredAuthData();

    this.initialized = true;
  }

  private async handleMobileAuthEvent(event: CustomEvent<MobileAuthData>): Promise<void> {
    try {
      const authData = event.detail;
      await this.processAuthData(authData, false); // Don't show welcome message - AuthModalWrapper already handles this
    } catch (error) {
      console.error('[MobileAuthService] Error handling mobile auth event:', error);
      
      toast({
        title: "Authentication Error",
        description: "Failed to sync mobile authentication. Please try signing in again.",
        variant: "destructive",
      });
    }
  }

  private async checkStoredAuthData(): Promise<void> {
    try {
      const storedData = localStorage.getItem('firebase-mobile-auth');
      if (!storedData) return;

      const authData: MobileAuthData = JSON.parse(storedData);
      // Check if data is still valid (within 1 hour)
      const authTimestamp = new Date(authData.timestamp);
      const now = new Date();
      const diffHours = (now.getTime() - authTimestamp.getTime()) / (1000 * 60 * 60);

      if (diffHours > 1) {
        this.clearStoredAuthData();
        return;
      }

      // If user is not currently authenticated, try to restore their session
      if (!auth.currentUser) {
        await this.processAuthData(authData, false); // Don't show welcome message for restoration
      }
    } catch (error) {
      console.error('[MobileAuthService] Error checking stored auth data:', error);
      this.clearStoredAuthData();
    }
  }

  private async processAuthData(authData: MobileAuthData, showWelcome: boolean = false): Promise<void> {
    try {
      // Check if user exists in our system
      const userProfile = await getUserProfile(authData.uid);
      if (!userProfile) {
        throw new Error('User profile not found in database');
      }

      // Validate the user's email matches what we have on record
      if (userProfile.email !== authData.email) {
        throw new Error('Email mismatch in authentication data');
      }

      // At this point, we know the user is legitimate but we can't directly
      // authenticate them in the web app using just the ID token.
      // We'll set up a state that indicates the user should be considered authenticated
      this.setAuthenticatedState(authData);

      // Only show welcome message for explicit authentication, not restoration
      if (showWelcome) {
        toast({
          title: "Welcome Back!",
          description: `You are signed in as ${authData.email}`,
        });
      }

      console.log('[MobileAuthService] Mobile authentication processed successfully');
    } catch (error) {
      console.error('[MobileAuthService] Error processing auth data:', error);
      throw error;
    }
  }

  private setAuthenticatedState(authData: MobileAuthData): void {
    // Store validated auth state
    sessionStorage.setItem('mobile-auth-validated', JSON.stringify({
      uid: authData.uid,
      email: authData.email,
      timestamp: new Date().toISOString()
    }));

    // Update the stored auth data with validation
    const updatedAuthData = { ...authData, validated: true };
    localStorage.setItem('firebase-mobile-auth', JSON.stringify(updatedAuthData));
  }

  public isAuthenticated(): boolean {
    if (auth.currentUser) return true;

    // Check for validated mobile auth
    const validatedAuth = sessionStorage.getItem('mobile-auth-validated');
    if (!validatedAuth) return false;

    try {
      const authData = JSON.parse(validatedAuth);
      const authTimestamp = new Date(authData.timestamp);
      const now = new Date();
      const diffMinutes = (now.getTime() - authTimestamp.getTime()) / (1000 * 60);

      // Consider authenticated for 30 minutes
      return diffMinutes < 30;
    } catch {
      return false;
    }
  }

  public getAuthenticatedUser(): { uid: string; email: string } | null {
    if (auth.currentUser) {
      return {
        uid: auth.currentUser.uid,
        email: auth.currentUser.email || ''
      };
    }

    const validatedAuth = sessionStorage.getItem('mobile-auth-validated');
    if (!validatedAuth) return null;

    try {
      const authData = JSON.parse(validatedAuth);
      return {
        uid: authData.uid,
        email: authData.email
      };
    } catch {
      return null;
    }
  }

  private clearStoredAuthData(): void {
    localStorage.removeItem('firebase-mobile-auth');
    sessionStorage.removeItem('firebase-token');
    sessionStorage.removeItem('mobile-auth-validated');
  }

  public signOut(): void {
    this.clearStoredAuthData();
    
    // Set recent logout flag to prevent unwanted auth modals
    sessionStorage.setItem('recent_logout', Date.now().toString());
    
    // Clear auth cookies
    document.cookie = 'firebase-token=; path=/; domain=.jampackd.store; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = 'firebase-token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  }
}

// Export singleton instance
export const mobileAuthService = MobileAuthService.getInstance();

// Export convenience functions
export const isMobileAuthenticated = () => mobileAuthService.isAuthenticated();
export const getMobileAuthUser = () => mobileAuthService.getAuthenticatedUser();